export const ANDROID_WEBM_OPUS_MIME_TYPE = "audio/webm;codecs=opus";
const NATIVE_RECORDER_START_TIMEOUT = 3000;

type NativeRecorderState = "inactive" | "recording";
export type NativeRecorderDiagnosticEvent =
    | "native:start:before"
    | "native:start:after"
    | "native:start:onstart"
    | "native:start:timeout"
    | "native:start:error"
    | "native:stop:before"
    | "native:stop:after"
    | "native:stop:onstop"
    | "native:stop:error";

type NativeRecorderDiagnosticHandler = (event: NativeRecorderDiagnosticEvent, data?: Record<string, unknown>) => void;

export class NativeMediaRecorder {
    private readonly mediaRecorder: MediaRecorder;
    private readonly onDiagnostic?: NativeRecorderDiagnosticHandler;
    private readonly chunks: Blob[] = [];
    private blob?: Blob;

    constructor(stream: MediaStream, onDiagnostic?: NativeRecorderDiagnosticHandler) {
        this.onDiagnostic = onDiagnostic;
        this.mediaRecorder = new MediaRecorder(stream, {
            mimeType: ANDROID_WEBM_OPUS_MIME_TYPE
        });

        this.mediaRecorder.ondataavailable = (event): void => {
            if (event.data?.size) {
                this.chunks.push(event.data);
            }
        };
    }

    static isSupported(): boolean {
        return (
            typeof MediaRecorder !== "undefined" &&
            typeof MediaRecorder.isTypeSupported === "function" &&
            MediaRecorder.isTypeSupported(ANDROID_WEBM_OPUS_MIME_TYPE)
        );
    }

    async startRecording(): Promise<void> {
        this.chunks.length = 0;
        this.blob = undefined;

        await new Promise<void>((resolve, reject) => {
            const cleanup = (): void => {
                window.clearTimeout(timeoutId);
                this.mediaRecorder.removeEventListener("start", handleStart);
                this.mediaRecorder.removeEventListener("error", handleError);
            };

            const handleStart = (): void => {
                this.track("native:start:onstart");
                cleanup();
                resolve();
            };

            const handleError = (event: Event): void => {
                this.track("native:start:error", { error: event.type });
                cleanup();
                reject(event);
            };

            this.mediaRecorder.addEventListener("start", handleStart);
            this.mediaRecorder.addEventListener("error", handleError);

            const timeoutId = window.setTimeout(() => {
                this.track("native:start:timeout");
                cleanup();
                reject(new Error("Native recorder start timeout"));
            }, NATIVE_RECORDER_START_TIMEOUT);

            try {
                this.track("native:start:before");
                this.mediaRecorder.start();
                this.track("native:start:after");
            } catch (error) {
                this.track("native:start:error", { error: error instanceof Error ? error.message : `${error}` });
                cleanup();
                reject(error);
            }
        });
    }

    async stopRecording(): Promise<void> {
        if (this.mediaRecorder.state === "inactive") {
            this.createBlob();
            return;
        }

        await new Promise<void>((resolve, reject) => {
            this.mediaRecorder.onerror = (event): void => {
                this.track("native:stop:error", { error: event.type });
                reject(event);
            };
            this.mediaRecorder.onstop = (): void => {
                this.track("native:stop:onstop");
                this.createBlob();
                resolve();
            };
            this.track("native:stop:before");
            this.mediaRecorder.stop();
            this.track("native:stop:after");
        });
    }

    async getBlob(): Promise<Blob | undefined> {
        if (!this.blob) {
            this.createBlob();
        }

        return this.blob;
    }

    async reset(): Promise<void> {
        this.chunks.length = 0;
        this.blob = undefined;
    }

    async destroy(): Promise<void> {
        if (this.mediaRecorder.state !== "inactive") {
            await this.stopRecording();
        }

        await this.reset();
    }

    async getState(): Promise<NativeRecorderState> {
        return this.mediaRecorder.state === "recording" ? "recording" : "inactive";
    }

    getRecorderType(): string {
        return "native-media-recorder";
    }

    private createBlob(): void {
        this.blob = new Blob(this.chunks, {
            type: ANDROID_WEBM_OPUS_MIME_TYPE
        });
    }

    private track(event: NativeRecorderDiagnosticEvent, data?: Record<string, unknown>): void {
        this.onDiagnostic?.(event, {
            ...data,
            recorderState: this.mediaRecorder.state
        });
    }
}
